import { apiService } from '../api';
import { BackupInfo, BackupListResponse, BackupCreateRequest, BackupCreateResponse, BackupRestoreRequest, BackupRestoreResponse, BackupDeleteRequest, BackupDeleteResponse, BackupStatsResponse } from '../../types/backup';

/**
 * Backup Service
 * 
 * Service for interacting with database backup API endpoints.
 * Provides functionality to list, create, restore, and delete backups.
 */

export interface BackupFilters {
  // Add filters if needed in the future
}

class BackupService {
  private readonly api = apiService;

  /**
   * Get list of all available backups
   */
  async getBackups(filters?: BackupFilters): Promise<BackupListResponse> {
    try {
      const response = await this.api.get('/api/v1/backups/');
      return response as BackupListResponse;
    } catch (error) {
      console.error('Error fetching backups:', error);
      throw error;
    }
  }

  /**
   * Get backup statistics
   */
  async getBackupStats(): Promise<BackupStatsResponse> {
    try {
      const response = await this.api.get('/api/v1/backups/stats');
      return response as BackupStatsResponse;
    } catch (error) {
      console.error('Error fetching backup stats:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific backup
   */
  async getBackupInfo(filename: string): Promise<BackupInfo> {
    try {
      const response = await this.api.get(`/api/v1/backups/${encodeURIComponent(filename)}`);
      return response as BackupInfo;
    } catch (error) {
      console.error('Error fetching backup info:', error);
      throw error;
    }
  }

  /**
   * Create a new backup
   */
  async createBackup(request: BackupCreateRequest = { compress: true }): Promise<BackupCreateResponse> {
    try {
      const response = await this.api.post('/api/v1/backups/', request);
      return response as BackupCreateResponse;
    } catch (error) {
      console.error('Error creating backup:', error);
      throw error;
    }
  }

  /**
   * Restore database from a backup
   * WARNING: This is a destructive operation!
   */
  async restoreBackup(filename: string, confirm: boolean = false): Promise<BackupRestoreResponse> {
    try {
      const request: BackupRestoreRequest = { filename, confirm };
      const response = await this.api.post('/api/v1/backups/restore', request);
      return response as BackupRestoreResponse;
    } catch (error) {
      console.error('Error restoring backup:', error);
      throw error;
    }
  }

  /**
   * Delete a backup file
   */
  async deleteBackup(filename: string, confirm: boolean = false): Promise<BackupDeleteResponse> {
    try {
      const request: BackupDeleteRequest = { filename, confirm };
      const response = await (this.api as any).deleteWithData('/api/v1/backups/', request);
      return response as BackupDeleteResponse;
    } catch (error) {
      console.error('Error deleting backup:', error);
      throw error;
    }
  }

  /**
   * Refresh the backups list
   */
  async refreshBackups(): Promise<BackupListResponse> {
    return this.getBackups();
  }

  /**
   * Get formatted backup size
   */
  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  /**
   * Get backup age in human-readable format
   */
  getBackupAge(created: string): string {
    const createdDate = new Date(created);
    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60)) % 24;
    const diffMinutes = Math.floor(diffMs / (1000 * 60)) % 60;

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }
}

// Export singleton instance
export const backupService = new BackupService();
